import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  Users,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import api from '../../utils/axios';
import formatCurrency from '../../helper/formatCurrency';




const Analytics = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchData = async () => {
    setLoading(true);
    const res = await api.get('/getDashboardData');
    setData(res.data?.data ?? {});
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <div className="p-10">Loading analytics...</div>;

  /* ================= APEX CHART ================= */

  const chartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '50%',
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: ['Deposits', 'Withdrawals'],
    },
    colors: ['#22c55e', '#ef4444'],
    tooltip: {
      y: {
        formatter: (val: number) => `$${val}`,
      },
    },
  };

  const chartSeries = [
    {
      name: 'Amount',
      data: [
        data?.totalDeposits ?? 0,
        data?.totalWithdrawals ?? 0,
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Analytics</h1>
            <p className="text-slate-500">
              Platform financial insights
            </p>
          </div>

          <div className="flex gap-3">
            <div className="flex items-center gap-2 text-sm bg-slate-100 px-4 py-2 rounded-lg">
              <Calendar size={16} />
              {lastUpdated.toLocaleTimeString()}
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI ROW */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Kpi title="Users" value={data.totalUsers} icon={<Users />} />
          <Kpi title="Deposits" value={formatCurrency(data.totalDeposits)} icon={<ArrowUpCircle />} />
          <Kpi title="Withdrawals" value={formatCurrency(data.totalWithdrawals)} icon={<ArrowDownCircle />} />
          <Kpi
            title="Net Revenue"
            value={formatCurrency((data.totalDeposits ?? 0) - (data.totalWithdrawals ?? 0))}
            icon={<TrendingUp />}
          />
        </div>

        {/* CHART */}
        <div className="bg-white border rounded-2xl p-6 shadow-md">
          <h2 className="text-lg font-bold mb-4">
            Deposits vs Withdrawals
          </h2>
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={350}
          />
        </div>

      </div>
    </div>
  );
};

/* ================= KPI CARD ================= */

const Kpi = ({
  title,
  value,
  icon,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
}) => (
  <div className="bg-white p-6 rounded-xl border shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-slate-500">{title}</p>
      <div className="text-slate-400">{icon}</div>
    </div>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default Analytics;
